import { ArrowLeftIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import {
  ordered,
  useAcknowledgeDiscrepancy,
  useConclusion,
  type Conclusion,
} from '@entities/conclusion';
import { useSearchSession } from '@entities/search-session';
import { useSelection } from '@entities/selection';
import { KarlDialogue } from '@features/chat';
import {
  ConclusionDetails,
  FinalCheckBlock,
  NoRecommendation,
  RecommendationBanner,
} from '@features/conclusion';
import { ApiError } from '@shared/api/problem';
import { ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import { money } from '@shared/lib/money';
import { Button, ButtonLink, KarlIntro, NextStepAction, Skeleton, StateBlock } from '@shared/ui';

import styles from './ConclusionPage.module.css';

/**
 * C-01 «Заключение по сделке» — шаг 6 воронки (FE-250, MVP_PRD §38.2, §38.4).
 *
 * Экран отвечает на один вопрос: **что получилось по сделке** — и показывает
 * основания, по которым на него отвечено.
 *
 * Пять правил экрана:
 *
 * * **результат торга — фактами и цитатами**: цена объявления, финальная цена,
 *   разница, полученные сверх цены позиции. Каждая позиция существует только с
 *   цитатой автохауса (§42.3);
 * * **блок финальной сверки над действиями** (§38.3). Существенное расхождение
 *   приостанавливает согласие до подтверждения, и подтверждение означает ровно
 *   «я это видел» — не согласие с расхождением;
 * * **решение принимается в переписке.** Кнопки «Согласиться» и «Отказаться»
 *   живут там же, где письмо и предложение, — разрывать этот контекст нельзя
 *   (§22.7). Отсюда на них ведёт ссылка;
 * * **исход «не рекомендуем ни один» — полноценный путь**, а не пустой экран:
 *   названа причина и предложены выходы (§38.4);
 * * **ни одного утверждения о состоянии автомобиля и о продавце.** Заключение
 *   не говорит «автомобиль исправен», «продавец надёжен» и «сделка безопасна»:
 *   таких выводов CarViQ.DE не делает (§2).
 *
 * Пустой набор — «торг ещё не дошёл до финального предложения»: это ожидание, а
 * не ошибка и не исход.
 *
 * Редизайн 2026-10 (DESIGN_SYSTEM §5.1, §5.2, §7.2): вводный абзац — реплика
 * Karl (`KarlIntro`); блоки заключения — отдельные карточки одной колонкой, без
 * карточки-обёртки (вложенность не глубже двух уровней). Красная кнопка одна —
 * «к сопровождению», когда согласие уже дано; переход к решению в переписке —
 * действие на карточке, синий контур (`outline-action`): само решение живёт
 * там, а не здесь.
 */
export function ConclusionPage() {
  const { t, i18n } = useTranslation();
  const { searchId = '' } = useParams();
  const locale = i18n.resolvedLanguage ?? 'de';

  const sessionQuery = useSearchSession(searchId || null);
  const conclusionQuery = useConclusion(searchId || null);
  const selectionQuery = useSelection(searchId || null);
  const acknowledge = useAcknowledgeDiscrepancy(searchId);

  const session = sessionQuery.data ?? null;
  useAppShell({
    stage: session?.session.stage ?? null,
    paid: session?.session.paid ?? false,
    karl: { extra: <KarlDialogue searchId={searchId} /> },
    // Шаг 5 трекера — переписка выбранного автомобиля: она уже есть в составе.
    negotiationId:
      selectionQuery.data?.slots.find((slot) => slot.status === 'selected')?.negotiation_id ?? null,
  });

  if (conclusionQuery.isPending) {
    return (
      <div className={styles.page} aria-hidden="true">
        <Skeleton height="2.5rem" width="45%" />
        <Skeleton height="18rem" radius="var(--radius-card)" />
      </div>
    );
  }

  if (conclusionQuery.isError) {
    const unpaid =
      conclusionQuery.error instanceof ApiError && conclusionQuery.error.status === 402;
    return (
      <div className={styles.page}>
        <StateBlock
          tone={unpaid ? 'neutral' : 'error'}
          title={unpaid ? t('selection.locked.title') : t('conclusion.error.title')}
          description={unpaid ? t('selection.locked.text') : t('conclusion.error.text')}
          action={
            unpaid ? (
              <ButtonLink to={ROUTES.searchPayment(searchId)}>
                {t('selection.locked.action')}
              </ButtonLink>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  void conclusionQuery.refetch();
                }}
              >
                {t('error.retry')}
              </Button>
            )
          }
        />
      </div>
    );
  }

  const set = conclusionQuery.data;
  const items = ordered(set.items);
  const recommended = items.find((item) => item.is_recommended) ?? null;
  const frozen = selectionQuery.data?.frozen ?? true;

  return (
    <div className={styles.page}>
      <Link to={ROUTES.searchTop4(searchId)} className={styles.back}>
        <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
        {t('conclusion.backToTop4')}
      </Link>

      <header className={styles.header}>
        <h1 className={styles.title}>{t('conclusion.title')}</h1>
        {/* Вводный абзац — реплика Karl (DESIGN_SYSTEM §5.4, §7.2): текст
            фиксированный, из локалей, LLM его не пишет. */}
        <KarlIntro>{t('conclusion.subtitle')}</KarlIntro>
      </header>

      {/* Согласие уже дано — заказ на шаге 7, и отсюда туда ведёт единственная
          ссылка (FE-260). Без неё пользователь, принявший предложение, остаётся
          на экране решения, которое он уже принял. */}
      {session?.session.stage === 'handover' || session?.session.stage === 'completed' ? (
        <div className={styles.lead}>
          <NextStepAction to={ROUTES.searchHandover(searchId)}>
            {t('conclusion.goToHandover')}
          </NextStepAction>
        </div>
      ) : null}

      {items.length === 0 ? (
        <StateBlock
          tone="neutral"
          title={t('conclusion.waiting.title')}
          description={t('conclusion.waiting.text')}
          action={
            <ButtonLink to={ROUTES.negotiations} variant="secondary">
              {t('conclusion.waiting.action')}
            </ButtonLink>
          }
        />
      ) : null}

      {recommended === null || set.no_recommendation_reason !== null ? null : (
        <RecommendationBanner
          recommendation={
            recommended.recommendation ?? {
              vehicle_id: recommended.vehicle_id,
              code: '',
              params: {},
            }
          }
          conclusion={recommended}
          weights={set.weights}
          vehicleLabel={t('conclusion.thisVehicle')}
          algorithmVersion={set.algorithm_version}
        />
      )}

      {set.no_recommendation_reason === null ? null : (
        <NoRecommendation
          reason={set.no_recommendation_reason}
          searchId={searchId}
          canReplace={!frozen}
        />
      )}

      {items.map((item) => (
        <article key={item.conclusion_id} className={styles.card}>
          <DealResult conclusion={item} locale={locale} />

          {item.final_check === null ? null : (
            <FinalCheckBlock
              check={item.final_check}
              pending={acknowledge.isPending}
              onAcknowledge={() => {
                acknowledge.mutate(item.vehicle_id);
              }}
            />
          )}

          <ConclusionDetails conclusion={item} />

          {/* Решение живёт в переписке: там письмо, предложение и цитаты, и
              разрывать этот контекст нельзя (§22.7). */}
          {item.negotiation_id === null ? null : (
            <div className={styles.cardActions}>
              <ButtonLink variant="outline-action" to={ROUTES.negotiation(item.negotiation_id)}>
                {t('conclusion.goToDecision')}
              </ButtonLink>
            </div>
          )}
        </article>
      ))}

      {/* Обязательная граница §2 и §39.2: договор заключается напрямую. */}
      <p className={styles.disclaimer}>{t('conclusion.disclaimer')}</p>
    </div>
  );
}

/**
 * Результат торга: цены, разница и полученное сверх цены (§38.2, пункт 7).
 *
 * Разница показывается **со знаком**: «−1 500 €» и «1 500 €» — разные факты.
 * Позиции сверх цены существуют только с цитатой: денежной оценки у них нет и
 * не заводится (§42.3).
 */
function DealResult({ conclusion, locale }: { conclusion: Conclusion; locale: string }) {
  const { t } = useTranslation();

  return (
    <section className={styles.result} aria-label={t('conclusion.result.title')}>
      <h2 className={styles.resultTitle}>{t('conclusion.result.title')}</h2>

      <dl className={styles.figures}>
        <div>
          <dt>{t('conclusion.result.finalPrice')}</dt>
          <dd>
            {conclusion.final_price_cents === null
              ? t('common.noData')
              : money(conclusion.final_price_cents, 'EUR', locale, 'auto')}
          </dd>
        </div>
        <div>
          <dt>{t('conclusion.result.delta')}</dt>
          <dd>
            {conclusion.delta_vs_listing_cents === null
              ? t('common.noData')
              : money(conclusion.delta_vs_listing_cents, 'EUR', locale, 'auto')}
          </dd>
        </div>
      </dl>

      {conclusion.gained_conditions.length === 0 ? null : (
        <>
          <h3 className={styles.resultSub}>{t('conclusion.result.gained')}</h3>
          <ul className={styles.gained}>
            {conclusion.gained_conditions.map((item) => (
              <li key={`${item.code}:${item.quote}`}>
                {/* `code` — слова автохауса (`label`), а не код CarViQ.DE. */}
                <span className={styles.gainedLabel}>{item.code}</span>
                <blockquote className={styles.quote}>{item.quote}</blockquote>
              </li>
            ))}
          </ul>
          <p className={styles.note}>{t('conclusion.result.gainedNote')}</p>
        </>
      )}
    </section>
  );
}
